import { useEffect, useState } from "react";
import ContentForm from "../../../../Components/ContentForm";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Switch from "@material-ui/core/Switch";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useDebouncedValue } from "../../../../hooks/useDebounce";

import { maskCep, removeMaskCep } from "../../../../utils/maskCep";
import { useHistory, useParams } from "react-router-dom";
import { useBreadcrumbs } from "../../../../contexts/breadcrumb";
import Domain from "@material-ui/icons/Domain";
import { useAttendanceLocalDetails } from "../../../../services/api-tansquery/attendance-local/details";
import { updateAttendanceLocalSchema, UpdateLocalSchemaData } from "./schema";
import FormSkeleton, {
    FieldType,
} from "../../../../Components/Skeletons/form-skeleton";
import { InputAdornment, IconButton } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useCidadesSearch } from "../../../../services/api-tansquery/get-list-cities";
import { useCidadeBySlug } from "../../../../services/api-tansquery/get-city-slug";
import {
    useAttendanceLocalUpdate,
    AttendanceLocalUpdateData,
} from "../../../../services/api-tansquery/attendance-local/update";
import OrganField from "../components/organ-field";

const fields: FieldType[] = ["text", "select", "switch", "text"];

export default function EditAttendanceLocals() {
    const { slug } = useParams<{ slug: string }>();
    const history = useHistory();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [open, setOpen] = useState(false);
    const [city, setCity] = useState<string | undefined>(undefined);
    const [onlyPreview, setOnlyPreview] = useState<boolean>(true);

    const debouncedCity = useDebouncedValue(city, 500);

    const handleSwitchValueCity = () =>
        setOnlyPreview((oldPreview) => !oldPreview);

    const { data, isFetching: loading } = useAttendanceLocalDetails(slug);

    const { isFetching, cityList } = useCidadesSearch({
        cidade: debouncedCity, estado: "Mato Grosso do Sul"
    });

    const { response: oneCity, isLoading: loadingCity } = useCidadeBySlug({
        slug: data?.cidade?.slug,
        enabled: !!data?.cidade,
    });

    const { mutate: handleUpdateAttendanceFn, isLoading } =
        useAttendanceLocalUpdate(slug);

    const { control, handleSubmit, reset } = useForm<UpdateLocalSchemaData>({
        resolver: zodResolver(updateAttendanceLocalSchema),
    });

    const onUpdateSubmit = (data: UpdateLocalSchemaData) => {
        const { cidade, ...rest } = data;

        const payload = {
            ...rest,
            cep: removeMaskCep(data.cep),
            cidade_id: cidade?.slug,
        };

        handleUpdateAttendanceFn(payload as AttendanceLocalUpdateData);
    };

    useEffect(() => {
        setBreadcrumbs((prev) => {
            const homeBreadcrumb = prev.find((_, index) => index === 0);
            return [
                homeBreadcrumb,
                {
                    label: "Locais de Atendimento",
                    path: "/locais-de-atendimento",
                    icon: Domain,
                },
                {
                    label: "Gerenciar",
                    path: "",
                },
            ];
        });
    }, [setBreadcrumbs]);

    useEffect(() => {
        if (data && oneCity) {
            reset({
                nome: data.nome,
                ativo: data.ativo,
                endereco: data.endereco,
                bairro: data.bairro,
                slug_unidade: data.slug_unidade,
                source: data.source,
                complemento: data.complemento,
                cep: data.cep,
                orgao_id: data.orgao.id,
                cidade: {
                    slug: oneCity.slug,
                    cidade: oneCity.cidade,
                    estado: oneCity.estado,
                },
                horario_unidade: data.horario_unidade || null,
            });
        }
    }, [data, reset, oneCity]);

    return (
        <ContentForm label="Gerenciar Local de Atendimento">
            {loading || loadingCity ? (
                <FormSkeleton fields={fields} />
            ) : (
                <form onSubmit={handleSubmit(onUpdateSubmit)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="nome"
                                defaultValue=""
                                control={control}
                                rules={{ required: "Nome é obrigatório" }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        required
                                        label="Nome"
                                        value={field.value ?? ""}
                                        variant="outlined"
                                        fullWidth
                                        error={!!fieldState.error}
                                        helperText={
                                            fieldState.error?.message ||
                                            "Informe o nome do local de atendimento"
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="slug_unidade"
                                control={control}
                                defaultValue=""
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Identificador Externo"
                                        variant="outlined"
                                        fullWidth
                                        value={field.value ?? ""}
                                        error={!!fieldState.error}
                                        helperText={
                                            fieldState.error?.message ||
                                            "Informe o identificador externo (pode ser usado para integrações)"
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Controller
                                name="cep"
                                control={control}
                                defaultValue=""
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        value={maskCep(field?.value) ?? ""}
                                        label="CEP"
                                        variant="outlined"
                                        required
                                        error={!!fieldState.error}
                                        helperText={
                                            fieldState.error?.message ||
                                            "Informe o CEP do local de atendimento"
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="endereco"
                                control={control}
                                defaultValue=""
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Endereço"
                                        value={field.value ?? ""}
                                        variant="outlined"
                                        fullWidth
                                        required
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="bairro"
                                control={control}
                                defaultValue=""
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Bairro"
                                        variant="outlined"
                                        value={field.value ?? ""}
                                        fullWidth
                                        required
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl required variant="outlined" fullWidth>
                                {oneCity && onlyPreview ? (
                                    <Controller
                                        name="cidade"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                label="Cidade"
                                                variant="outlined"
                                                fullWidth
                                                required
                                                value={
                                                    field.value
                                                        ? `${field.value.cidade} - ${field.value.estado}`
                                                        : ""
                                                }
                                                InputProps={{
                                                    readOnly: true,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => {
                                                                    // Troca para Autocomplete
                                                                    handleSwitchValueCity();
                                                                    // Zera o campo no form
                                                                    field.onChange(null);
                                                                }}
                                                                edge="end"
                                                                size="small"
                                                            >
                                                                <Close />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                ) : (
                                    <Controller
                                        name="cidade"
                                        control={control}
                                        render={({ field }) => (
                                            <Autocomplete
                                                {...field}
                                                loadingText="Carregando..."
                                                open={open}
                                                onOpen={() => setOpen(true)}
                                                onClose={() => {
                                                    setOpen(false);
                                                    setCity(null);
                                                }}
                                                options={cityList}
                                                value={field.value}
                                                getOptionSelected={(option, value) =>
                                                    option.slug === value.slug
                                                }
                                                loading={isFetching}
                                                getOptionLabel={(option) => `${option?.cidade} - ${option?.estado}` || ""}
                                                onChange={(_, value) => field.onChange(value)}
                                                onInputChange={(_, value, reason) => {
                                                    if (reason === "input") {
                                                        setCity(value);
                                                    }
                                                }}
                                                noOptionsText="Sem resultados"
                                                renderInput={(params) => (
                                                    <TextField
                                                        required
                                                        {...params}
                                                        label="Cidade"
                                                        variant="outlined"
                                                        InputLabelProps={{ shrink: true }}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            endAdornment: (
                                                                <>
                                                                    {isFetching ? (
                                                                        <CircularProgress
                                                                            color="inherit"
                                                                            size={20}
                                                                        />
                                                                    ) : null}
                                                                    {params.InputProps.endAdornment}
                                                                </>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="complemento"
                                control={control}
                                defaultValue=""
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Complemento"
                                        variant="outlined"
                                        fullWidth
                                        value={field.value ?? ""}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <Controller
                                name="source"
                                control={control}
                                defaultValue=""
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Mapa(source)"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        value={field.value ?? ""}
                                        error={!!fieldState.error}
                                        helperText={
                                            fieldState.error?.message ||
                                            "Informe a URL do source do mapa"
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <FormControl variant="outlined" fullWidth>
                                <Controller
                                    name="orgao_id"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <OrganField
                                            field={field}
                                            fieldState={fieldState}
                                            organ={data?.orgao}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="ativo"
                                control={control}
                                defaultValue={false}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={<Switch {...field} checked={field.value} />}
                                        label="Este local está ativo?"
                                    />
                                )}
                            />
                            <FormHelperText>
                                Ative esta opção para permitir que este local seja exibido como
                                disponível para agendamentos no sistema.
                            </FormHelperText>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
                        >
                            <Button
                                onClick={() => history.goBack()}
                                variant="text"
                            >
                                Cancelar
                            </Button>
                            <Button
                                disabled={isLoading}
                                type="submit"
                                variant="contained"
                                color="primary"
                            >
                                {!isLoading ? "Salvar" : <CircularProgress />}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            )}
        </ContentForm>
    );
}
